import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "../../api";
import { persistAuthSession } from "../../helpers/authSession";
import { REGISTRATION_PLAN_SELECTED_EVENT } from "../../helpers/publicRegistrationPlan";

const RegistrationForm = () => {
    const [formData, setFormData] = useState({
        name: "",
        email: "",
        password: "",
        passwordConfirmation: "",
        store_name: "",
        planId: "",
    });
    const [selectedPlan, setSelectedPlan] = useState(null);

    const [errors, setErrors] = useState({
        passwordError: "",
        passwordConfirmationError: "",
        registrationError: "",
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [registrationSuccess, setRegistrationSuccess] = useState("");
    const [acceptedTerms, setAcceptedTerms] = useState(false);

    useEffect(() => {
        const handlePlanSelected = (event) => {
            const plan = event?.detail;
            if (!plan?.id) {
                return;
            }
            setSelectedPlan(plan);
            setFormData((prev) => ({ ...prev, planId: plan.id }));
        };

        window.addEventListener(REGISTRATION_PLAN_SELECTED_EVENT, handlePlanSelected);
        return () => window.removeEventListener(REGISTRATION_PLAN_SELECTED_EVENT, handlePlanSelected);
    }, []);

    const registrationHeading = useMemo(() => {
        if (!selectedPlan || selectedPlan.isFree) {
            return 'Comece gerir sua produção e vendas agora mesmo';
        }
        return `Crie sua conta — plano ${selectedPlan.name}`;
    }, [selectedPlan]);

    const submitLabel = useMemo(() => {
        if (!selectedPlan || selectedPlan.isFree) {
            return 'Criar conta gratuita';
        }
        return `Criar conta — ${selectedPlan.name}`;
    }, [selectedPlan]);

    const handleInputChange = (e) => {
        const {name, value} = e.target;
        setFormData({...formData, [name]: value});
    };

    const validatePassword = () => {
        const {password} = formData;
        const minLength = password.length >= 8;
        const hasUppercase = /[A-Z]/.test(password);
        const hasLowercase = /[a-z]/.test(password);
        const hasNumber = /\d/.test(password);
        const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(password);

        let passwordValidationMessage = ``;

        if (!minLength) {
            passwordValidationMessage += `\n- Ao menos 8 caracteres`;
        }
        if (!hasUppercase) {
            passwordValidationMessage += `\n- Ao menos 1 letra maiúscula`;
        }
        if (!hasLowercase) {
            passwordValidationMessage += `\n- Ao menos 1 letra minúscula`;
        }
        if (!hasNumber) {
            passwordValidationMessage += `\n- Ao menos 1 número`;
        }
        if (!hasSpecialChar) {
            passwordValidationMessage += `\n- Ao menos 1 caracter especial`;
        }

        if (!minLength || !hasUppercase || !hasLowercase || !hasNumber || !hasSpecialChar) {
            setErrors((prev) => ({
                ...prev,
                passwordError: `A senha deve possuir:` + passwordValidationMessage,
            }));
            return false;
        }

        setErrors((prev) => ({...prev, passwordError: ""}));
        return true;
    };

    const passwordsMatch = () => {
        const {password, passwordConfirmation} = formData;
        if (password !== passwordConfirmation) {
            setErrors((prev) => ({
                ...prev,
                passwordConfirmationError: "As senhas não correspondem.",
            }));
            return false;
        }
        setErrors((prev) => ({...prev, passwordConfirmationError: ""}));
        return true;
    };

    const register = async (e) => {
        e.preventDefault();

        const isPasswordValid = validatePassword();
        const isPasswordConfirmed = passwordsMatch();

        if (!acceptedTerms) {
            setErrors((prev) => ({
                ...prev,
                registrationError: "Você precisa aceitar os Termos de Uso e a Política de Privacidade.",
            }));
            return;
        }

        if (!isPasswordValid || !isPasswordConfirmed) {
            setErrors((prev) => ({
                ...prev,
                registrationError: "Corrija os erros antes de continuar.",
            }));
            return;
        }

        setErrors((prev) => ({...prev, registrationError: ""}));
        setIsSubmitting(true);

        try {
            const response = await api.post("auth/register", {
                name: formData.name,
                email: formData.email,
                password: formData.password,
                store_name: formData.store_name,
                subscription_plan_uuid: selectedPlan && !selectedPlan.isFree ? (selectedPlan.id || '') : '',
            });

            if (response.data?.access_token) {
                persistAuthSession(response.data);
                window.location.href = '/admin';
                return;
            }

            setRegistrationSuccess(
                response.data?.message ||
                "Enviamos um e-mail para ativar sua conta. A entrega pode levar alguns minutos; verifique sua caixa de entrada e, se não encontrar, a pasta de spam ou lixo eletrônico."
            );
            setFormData({
                name: "",
                email: "",
                password: "",
                passwordConfirmation: "",
                store_name: "",
                planId: selectedPlan?.id || "",
            });
            setAcceptedTerms(false);
        } catch (error) {
            console.error("Erro ao registrar usuário:", error);
            const message =
                error.response?.data?.error ||
                error.response?.data?.message ||
                "Não foi possível concluir o cadastro.";
            setErrors((prev) => ({
                ...prev,
                registrationError: message,
            }));
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <section id="registrationForm">
            <div className="registration-form-inner">
                <h2 className="mb-4 mb-md-5 text-center" style={{ color: 'var(--public-ink)' }}>
                    {registrationHeading}
                </h2>

                {selectedPlan && !selectedPlan.isFree ? (
                    <div
                        className="alert alert-info mb-4"
                        role="status"
                        style={{ fontSize: '0.98rem', lineHeight: 1.55 }}
                    >
                        Plano selecionado: <strong>{selectedPlan.name}</strong>
                        {' '}(R$ {selectedPlan.price}/mês). Após ativar sua conta e fazer login, você será direcionado ao Mercado Pago para concluir a assinatura.
                        {' '}
                        <a href="#precos">Trocar plano</a>
                    </div>
                ) : null}

                {registrationSuccess ? (
                    <div
                        className="alert alert-success text-center"
                        role="status"
                        style={{ fontSize: '1.05rem', lineHeight: 1.6 }}
                    >
                        <p className="mb-3">{registrationSuccess}</p>
                        <a href="/admin/login" className="custom-btn font-weight-bold" style={{ display: 'inline-block', padding: '12px 24px' }}>
                            Ir para o login
                        </a>
                    </div>
                ) : (
                <form onSubmit={register} className="bg-white">
                    <input type="hidden" id="planId" name="planId" value={formData.planId} readOnly />
                    <div className="form-group mb-4">
                        <label htmlFor="name" className="font-weight-medium" style={{ fontSize: '1.1rem' }}>Nome</label>
                        <input
                            type="text"
                            className="form-control border-0 rounded-lg shadow-sm p-3"
                            id="name"
                            name="name"
                            value={formData.name}
                            onChange={handleInputChange}
                            required
                            placeholder="Precisamos saber quem é você"
                            style={{
                                backgroundColor: '#f1f3f5',
                                border: '1px solid #ced4da',
                                fontSize: '1rem',
                                transition: 'all 0.3s ease-in-out'
                            }}
                        />
                    </div>
                    <div className="form-group mb-4">
                        <label htmlFor="store_name" className="font-weight-medium" style={{ fontSize: '1.1rem' }}>Nome da sua loja (este nome aparecerá nos recibos dos clientes)</label>
                        <input
                            type="text"
                            className="form-control border-0 rounded-lg shadow-sm p-3"
                            id="store_name"
                            name="store_name"
                            value={formData.store_name}
                            onChange={handleInputChange}
                            required
                            placeholder="E também qual é o nome da sua loja"
                            style={{
                                backgroundColor: '#f1f3f5',
                                border: '1px solid #ced4da',
                                fontSize: '1rem',
                                transition: 'all 0.3s ease-in-out'
                            }}
                        />
                    </div>

                    <div className="form-group mb-4">
                        <label htmlFor="email" className="font-weight-medium" style={{ fontSize: '1.1rem' }}>Seu melhor e-mail</label>
                        <input
                            type="email"
                            className="form-control border-0 rounded-lg shadow-sm p-3"
                            id="email"
                            name="email"
                            value={formData.email}
                            onChange={handleInputChange}
                            required
                            placeholder="Informe aqui seu e-mail principal"
                            style={{
                                backgroundColor: '#f1f3f5',
                                border: '1px solid #ced4da',
                                fontSize: '1rem',
                                transition: 'all 0.3s ease-in-out'
                            }}
                        />
                    </div>

                    <div className="form-group mb-4">
                        <label htmlFor="password" className="font-weight-medium" style={{ fontSize: '1.1rem' }}>Senha</label>
                        <input
                            type="password"
                            className="form-control border-0 rounded-lg shadow-sm p-3"
                            id="password"
                            name="password"
                            value={formData.password}
                            onChange={handleInputChange}
                            required
                            placeholder="Crie uma senha de ao menos 8 caracteres"
                            onBlur={validatePassword}
                            style={{
                                backgroundColor: '#f1f3f5',
                                border: '1px solid #ced4da',
                                fontSize: '1rem',
                                transition: 'all 0.3s ease-in-out'
                            }}
                        />
                        {errors.passwordError && (
                            <div
                                className="text-danger mt-1"
                                style={{ whiteSpace: 'pre-wrap', fontSize: '0.9rem' }}
                            >
                                {errors.passwordError}
                            </div>
                        )}
                    </div>

                    <div className="form-group mb-4">
                        <label htmlFor="passwordConfirmation" className="font-weight-medium" style={{ fontSize: '1.1rem' }}>Confirme a senha</label>
                        <input
                            type="password"
                            className="form-control border-0 rounded-lg shadow-sm p-3"
                            id="passwordConfirmation"
                            name="passwordConfirmation"
                            value={formData.passwordConfirmation}
                            onChange={handleInputChange}
                            required
                            placeholder="E aqui repita essa senha para ter certeza que memorizou"
                            onBlur={passwordsMatch}
                            onPaste={(e) => e.preventDefault()}
                            autoComplete="off"
                            style={{
                                backgroundColor: '#f1f3f5',
                                border: '1px solid #ced4da',
                                fontSize: '1rem',
                                transition: 'all 0.3s ease-in-out'
                            }}
                        />
                        {errors.passwordConfirmationError && (
                            <div className="text-danger mt-1" style={{ fontSize: '0.9rem' }}>
                                {errors.passwordConfirmationError}
                            </div>
                        )}
                    </div>

                    <div className="form-group mb-4">
                        <div className="custom-control custom-checkbox">
                            <input
                                type="checkbox"
                                className="custom-control-input"
                                id="acceptTerms"
                                checked={acceptedTerms}
                                onChange={(e) => setAcceptedTerms(e.target.checked)}
                                required
                            />
                            <label
                                className="custom-control-label"
                                htmlFor="acceptTerms"
                                style={{ fontSize: '0.95rem', lineHeight: 1.5, color: '#475569' }}
                            >
                                Li e aceito os{' '}
                                <Link to="/termos-de-uso" target="_blank" rel="noopener noreferrer">
                                    Termos de Uso
                                </Link>{' '}
                                e a{' '}
                                <Link to="/politica-de-privacidade" target="_blank" rel="noopener noreferrer">
                                    Política de Privacidade
                                </Link>
                                .
                            </label>
                        </div>
                    </div>

                    {errors.registrationError && (
                        <div className="text-danger mt-3" style={{ fontSize: '1rem' }}>
                            {errors.registrationError}
                        </div>
                    )}

                    <button
                        className="custom-btn btn-block mt-4 font-weight-bold"
                        type="submit"
                        disabled={isSubmitting}
                        style={{
                            padding: '14px 24px',
                            fontSize: '1.125rem',
                            width: '100%',
                        }}
                    >
                        {isSubmitting ? 'Criando conta...' : submitLabel}
                    </button>
                </form>
                )}
            </div>
        </section>
    );
};

export default RegistrationForm;
